import { useEffect, useMemo, useRef, useState } from "react";
import type { DashboardFilters, DashboardWidgetRow, WidgetData } from "@astrum/shared";
import { Button, EmptyState, Icon, IconButton, Menu, Pill, SkeletonText, useToast } from "@astrum/ui";
import type { MenuItem } from "@astrum/ui";
import { api, ApiError } from "../lib/api.js";
import { exportChartPng, filterQuery, GROUP_LABEL, hoursLabel, METRIC_LABEL, moneyLabel, SIZE_LABEL, widgetTitle } from "../lib/dashboards.js";
import { downloadExcel, excelFileName } from "../lib/export-excel.js";
import { goalStatusLabel, goalStatusTone, progressLabel } from "../lib/goals.js";
import { BarChart, DonutChart, HBarChart, LineChart } from "./Charts.js";
import { Link } from "react-router";

type Props = {
  dashboardId: string;
  widget: DashboardWidgetRow;
  filters: DashboardFilters;
  canEdit: boolean;
  first: boolean;
  last: boolean;
  onEdit: () => void;
  onResize: (size: "S" | "M" | "L") => void;
  onMove: (direction: -1 | 1) => void;
  onRemove: () => void;
};

const STATUS_COLOR_VAR: Record<string, string> = { todo: "--faint", in_progress: "--info", review: "--warn", done: "--ok", cancelled: "--line-2" };
const SLICE_VARS = ["--accent", "--info", "--ok", "--warn", "--violet", "--danger", "--faint", "--line-2"];

const cssColor = (name: string): string =>
  (typeof window !== "undefined" && getComputedStyle(document.documentElement).getPropertyValue(name).trim()) || "#9ca3af";

const healthTone = (health: string) => (health === "off_track" ? "danger" : health === "at_risk" ? "warn" : "ok");

/** One widget: its card, its menu, and the figures the server computed for whoever is looking. */
export function DashboardWidgetView({ dashboardId, widget, filters, canEdit, first, last, onEdit, onResize, onMove, onRemove }: Props) {
  const { toast } = useToast();
  const bodyRef = useRef<HTMLDivElement>(null);
  const [data, setData] = useState<WidgetData | null>(null);
  const [state, setState] = useState<"loading" | "ok" | "forbidden" | "failed">("loading");
  const [offset, setOffset] = useState(0);
  const [menu, setMenu] = useState(false);

  const query = useMemo(() => filterQuery(filters), [filters]);
  const configKey = JSON.stringify(widget.config);
  const pageSize = (widget.config as { limit?: number }).limit ?? 10;
  const paged = ["workload", "time_logged", "budget_burn", "goals_progress", "project_health", "task_list"].includes(widget.type);

  // A new filter or setting starts again at the first page.
  useEffect(() => setOffset(0), [query, configKey]);

  useEffect(() => {
    let cancelled = false;
    setState((s) => (s === "ok" ? s : "loading"));
    const paging = paged ? `&limit=${pageSize}&offset=${offset}` : "";
    api
      .get<{ data: WidgetData }>(`/dashboards/${dashboardId}/widgets/${widget.id}/data?${query}${paging}`)
      .then((res) => {
        if (cancelled) return;
        setData(res.data);
        setState("ok");
      })
      .catch((error) => {
        if (cancelled) return;
        setState(error instanceof ApiError && error.status === 403 ? "forbidden" : "failed");
      });
    return () => {
      cancelled = true;
    };
  }, [dashboardId, widget.id, query, configKey, offset, paged, pageSize]);

  const title = widgetTitle(widget);

  const items: MenuItem[] = [
    {
      id: "png",
      label: "Download chart (PNG)",
      icon: "download",
      disabled: !["tasks_by", "completion_trend", "time_logged"].includes(widget.type) || state !== "ok",
      onSelect: () => {
        const el = bodyRef.current;
        void (el ? exportChartPng(el, `${title}.png`) : Promise.resolve(false)).then((ok) => {
          if (!ok) toast("This widget has no chart to save.", { tone: "danger" });
        });
      },
    },
    {
      id: "xlsx",
      label: "Download data (Excel)",
      icon: "download",
      disabled: state !== "ok",
      onSelect: () =>
        void downloadExcel(`/dashboards/${dashboardId}/widgets/${widget.id}/export?${query}`, excelFileName(title)).catch((error) =>
          toast(error instanceof ApiError ? error.message : "Could not export.", { tone: "danger" }),
        ),
    },
    ...(canEdit
      ? ([
          { id: "edit", label: "Settings", icon: "edit", onSelect: onEdit },
          ...(["S", "M", "L"] as const).map((size) => ({
            id: `size-${size}`,
            label: `Size: ${SIZE_LABEL[size]}${widget.size === size ? " (current)" : ""}`,
            icon: "settings" as const,
            disabled: widget.size === size,
            onSelect: () => onResize(size),
          })),
          { id: "up", label: "Move earlier", icon: "chevron-up", disabled: first, onSelect: () => onMove(-1) },
          { id: "down", label: "Move later", icon: "chevron-down", disabled: last, onSelect: () => onMove(1) },
          { id: "remove", label: "Remove", icon: "trash", destructive: true, onSelect: onRemove },
        ] as MenuItem[])
      : []),
  ];

  return (
    <section className={`dash-widget dash-widget--${widget.size}`} aria-label={title}>
      <div className="dash-widget__head">
        <h3 className="dash-widget__title">{title}</h3>
        <div className="dash-noprint">
          <Menu open={menu} onClose={() => setMenu(false)} label={`Actions for ${title}`} items={items}>
            <IconButton icon={<Icon name="more" size={14} />} label={`Actions for ${title}`} size="sm" onClick={() => setMenu((v) => !v)} />
          </Menu>
        </div>
      </div>
      <div className="dash-widget__body" ref={bodyRef}>
        {state === "loading" && !data ? (
          <SkeletonText lines={3} />
        ) : state === "forbidden" ? (
          <EmptyState icon="lock" title="Not available to you" description="Budget figures are for admins and the owners and managers of a project." />
        ) : state === "failed" ? (
          <EmptyState icon="warning" title="Could not load" description="Check your connection and try again." />
        ) : data ? (
          <Body data={data} chart={(widget.config as { chart?: string }).chart} />
        ) : null}
      </div>
      {state === "ok" && data && paged && "total" in data && data.total > pageSize && (
        <div className="dash-widget__pager dash-noprint">
          <Button size="sm" variant="ghost" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - pageSize))}>
            Previous
          </Button>
          <span>
            {offset + 1}–{Math.min(offset + pageSize, data.total)} of {data.total}
          </span>
          <Button size="sm" variant="ghost" disabled={offset + pageSize >= data.total} onClick={() => setOffset(offset + pageSize)}>
            Next
          </Button>
        </div>
      )}
    </section>
  );
}

function Rows({ rows }: { rows: { key: string; label: string; value: string; fraction?: number; level?: string }[] }) {
  return (
    <ul className="dash-rows">
      {rows.map((r) => (
        <li key={r.key}>
          <span className="dash-rows__label" title={r.label}>
            {r.label}
          </span>
          {r.fraction !== undefined && (
            <span className={`dash-bar ${r.level ? `dash-bar--${r.level}` : ""}`} aria-hidden="true">
              <span style={{ width: `${Math.min(100, Math.max(0, r.fraction * 100))}%` }} />
            </span>
          )}
          <span className="dash-rows__value">{r.value}</span>
        </li>
      ))}
    </ul>
  );
}

function Body({ data, chart }: { data: WidgetData; chart?: string }) {
  switch (data.type) {
    case "number_tile":
      return (
        <div>
          <div className={`dash-number ${data.metric === "overdue" && data.value > 0 ? "dash-number--danger" : ""}`}>{data.value}</div>
          <p className="dash-widget__note">{METRIC_LABEL[data.metric]}</p>
        </div>
      );
    case "tasks_by": {
      if (data.total === 0) return <EmptyState icon="insights" title="No tasks" description="Nothing matches the filters." />;
      const colored = data.groups.map((g, i) => ({
        label: g.label,
        value: g.value,
        color: cssColor(data.groupBy === "status" ? (STATUS_COLOR_VAR[g.key] ?? "--faint") : SLICE_VARS[i % SLICE_VARS.length]!),
      }));
      return (
        <>
          <ChartByKind groups={colored} kind={data} chart={chart} />
          <ul className="dash-legend">
            {data.groups.map((g) => (
              <li key={g.key}>
                <span>{g.label}</span>
                <span>{g.value}</span>
              </li>
            ))}
          </ul>
        </>
      );
    }
    case "completion_trend": {
      const completed = data.series.reduce((n, d) => n + d.completed, 0);
      const created = data.series.reduce((n, d) => n + d.created, 0);
      return (
        <>
          <LineChart data={data.series.map((d) => ({ label: d.day.slice(5), value: d.completed }))} height={180} title="Tasks completed per day" />
          <p className="dash-widget__note">
            {completed} completed, {created} created in this range.
          </p>
        </>
      );
    }
    case "workload":
      if (!data.people.length) return <EmptyState icon="people" title="Nobody to show" description="No one is planned in this range." />;
      return (
        <HBarChart
          title="Peak planned load per person"
          data={data.people.map((p) => ({
            label: `${p.displayName} · ${hoursLabel(p.capacityMinutes)}/wk`,
            value: p.peakPercent,
            max: 100,
            color: cssColor(p.peakPercent > 100 ? "--danger" : p.peakPercent > 85 ? "--warn" : "--accent"),
          }))}
        />
      );
    case "time_logged":
      if (!data.rows.length) return <EmptyState icon="clock" title="No time logged" description="Nothing was logged in this range." />;
      return (
        <>
          <BarChart data={data.rows.map((r) => ({ label: r.label, value: Math.round((r.minutes / 60) * 10) / 10 }))} height={180} title="Hours logged" />
          <p className="dash-widget__note">{hoursLabel(data.totalMinutes)} in total.</p>
        </>
      );
    case "budget_burn":
      if (!data.rows.length) return <EmptyState icon="info" title="No budgets" description="No project you manage has a budget set." />;
      return (
        <Rows
          rows={data.rows.map((r) => ({
            key: r.projectId,
            label: `${r.code} · ${r.name}`,
            value: `${moneyLabel(r.spentCents, r.currency)} of ${moneyLabel(r.budgetCents, r.currency)}`,
            fraction: (r.percent ?? 0) / 100,
            level: r.level === "ok" ? undefined : r.level,
          }))}
        />
      );
    case "goals_progress":
      if (!data.goals.length) return <EmptyState icon="insights" title="No goals" description="No goals in this period." />;
      return (
        <>
          {data.periodLabel && <p className="dash-widget__note">{data.periodLabel}</p>}
          <ul className="dash-rows">
            {data.goals.map((g) => (
              <li key={g.id}>
                <Link className="dash-rows__label" to="/goals" title={g.title}>
                  {g.title}
                </Link>
                <span className="dash-bar" aria-hidden="true">
                  <span style={{ width: `${g.progress ?? 0}%` }} />
                </span>
                <span className="dash-rows__value">{progressLabel(g.progress)}</span>
                <Pill tone={goalStatusTone(g.status)} dot>
                  {goalStatusLabel(g.status)}
                </Pill>
              </li>
            ))}
          </ul>
        </>
      );
    case "project_health":
      if (!data.projects.length) return <EmptyState icon="work" title="No projects" description="No projects match the filters." />;
      return (
        <ul className="dash-rows">
          {data.projects.map((p) => (
            <li key={p.id}>
              <Link className="dash-rows__label" to={`/work/project/${p.id}`} title={p.name}>
                {p.code} · {p.name}
              </Link>
              <span className="dash-bar" aria-hidden="true">
                <span style={{ width: `${p.progress}%` }} />
              </span>
              <span className="dash-rows__value">
                {p.progress}% · {p.overdueTasks} overdue
              </span>
              <Pill tone={healthTone(p.health)} dot>
                {goalStatusLabel(p.health)}
              </Pill>
            </li>
          ))}
        </ul>
      );
    case "task_list":
      if (!data.tasks.length) return <EmptyState icon="check" title="No tasks" description="Nothing matches this list's filters." />;
      return (
        <ul className="dash-rows">
          {data.tasks.map((t) => (
            <li key={t.id}>
              <Link className="dash-rows__label" to={`/work/task/${t.id}`} title={t.title}>
                {t.title}
              </Link>
              <span className="dash-rows__value">{t.projectCode ?? ""}</span>
              <span className="dash-rows__value">{t.dueAt ? new Date(t.dueAt).toLocaleDateString() : "No date"}</span>
            </li>
          ))}
        </ul>
      );
  }
}

function ChartByKind({ groups, kind, chart }: { groups: { label: string; value: number; color: string }[]; kind: Extract<WidgetData, { type: "tasks_by" }>; chart?: string }) {
  const title = `Tasks by ${GROUP_LABEL[kind.groupBy]?.toLowerCase()}`;
  return chart === "donut" ? (
    <DonutChart data={groups} title={title} />
  ) : (
    <BarChart data={groups.map((g) => ({ label: g.label, value: g.value }))} height={180} title={title} />
  );
}
